import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/ui/Icon';
import Footer from '../homepage/components/Footer';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (!email?.trim()) {
        throw new Error('Please enter your email address');
      }

      // Email validation regex
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex?.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      const { error: resetError } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window?.location?.origin}/reset-password`,
      });

      if (resetError) {
        throw new Error(resetError?.message || 'Failed to send reset email');
      }

      // Attempt to unlock account
      try {
         await supabase.rpc('reset_login_attempts', { p_user_id: (await supabase.from('users').select('id').eq('email', email).single()).data?.id });
      } catch (e) {
         // Ignore
      }

      setMessage('Password reset instructions have been sent to your email address. Please check your inbox.');
      setEmail(''); // Clear the form after success

    } catch (err) {
      console.error('Password reset error:', err);
      setError(err?.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Header */}
      <Header />

      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* Back Button */}
            <button
              onClick={() => navigate('/signin')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
            >
              <Icon name="ArrowLeft" size={20} className="mr-2" />
              <span className="text-sm">Back to Sign In</span>
            </button>

            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Reset Password</h2>
              <p className="text-gray-600 mt-2">
                Enter your email address and we'll send you instructions to reset your password
              </p>
            </div>

            {/* Success Message */}
            {message && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-start">
                <Icon name="CheckCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{message}</span>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start">
                <Icon name="AlertCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Reset Password Form */}
            <form onSubmit={handleResetPassword} className="space-y-4">
              <Input
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
                placeholder="email@example.com"
                disabled={loading}
              />

              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                iconName={loading ? "Loader2" : "Mail"}
                iconClassName={loading ? "animate-spin" : ""}
                iconPosition="right"
              >
                {loading ? 'Sending...' : 'Send Reset Instructions'}
              </Button>
            </form>

            {/* Additional Help */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Remember your password?{' '}
                <button
                  onClick={() => navigate('/signin')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Sign In
                </button>
              </p>
            </div>

            {/* Support Link */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <button
                  onClick={() => navigate('/support-center')}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  Contact Support
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
}